import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import {
    ChevronLeft, Plane, Bus, Hotel, CalendarDays, Calculator,
    Plus, Trash2, FileDown, Save, Eye, Library, Image as ImageIcon, Loader2, FileText,
    UserCircle2, Phone, Building2,
} from "lucide-react";
import ImageUploader, { resolveImageUrl } from "@/components/ImageUploader";
import { toast } from "sonner";

const formatMoney = (n, c = "CHF") => {
    const sym = { CHF: "CHF", EUR: "€", USD: "$" }[c] || c;
    return `${sym} ${Number(n || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const TABS = [
    { id: "overview", label: "Übersicht", icon: CalendarDays },
    { id: "flight", label: "Flug", icon: Plane },
    { id: "transfer", label: "Transfer", icon: Bus },
    { id: "hotel", label: "Hotel", icon: Hotel },
    { id: "itinerary", label: "Tagesplan", icon: CalendarDays },
    { id: "preview", label: "Timeline", icon: Eye },
    { id: "costs", label: "Kalkulation", icon: Calculator },
];

export default function TripEditorPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [trip, setTrip] = useState(null);
    const [excursions, setExcursions] = useState([]);
    const [hotelTemplates, setHotelTemplates] = useState([]);
    const [driverTemplates, setDriverTemplates] = useState([]);
    const [tab, setTab] = useState("overview");
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const saveTimer = useRef(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const [t, e, h, d] = await Promise.all([
                    api.get(`/trips/${id}`),
                    api.get(`/excursions`),
                    api.get(`/hotels`),
                    api.get(`/drivers`),
                ]);
                if (!mounted) return;
                setTrip(t.data);
                setExcursions(e.data);
                setHotelTemplates(h.data);
                setDriverTemplates(d.data);
            } catch (ex) {
                alert(formatApiError(ex));
                navigate("/dashboard");
            }
        })();
        return () => { mounted = false; };
    }, [id, navigate]);

    const update = (patch) => {
        setTrip((t) => ({ ...t, ...patch }));
        setDirty(true);
    };

    // Auto-save (debounced)
    useEffect(() => {
        if (!dirty || !trip) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            await save();
        }, 1200);
        return () => saveTimer.current && clearTimeout(saveTimer.current);
        // eslint-disable-next-line
    }, [trip, dirty]);

    const save = async () => {
        if (!trip) return;
        setSaving(true);
        try {
            const { data } = await api.put(`/trips/${id}`, {
                title: trip.title, client_name: trip.client_name, client_email: trip.client_email,
                destination: trip.destination, start_date: trip.start_date, end_date: trip.end_date,
                num_travelers: trip.num_travelers, num_adults: trip.num_adults, children_ages: trip.children_ages, currency: trip.currency, status: trip.status,
                outbound_flight: trip.outbound_flight, return_flight: trip.return_flight,
                extra_flights: trip.extra_flights,
                transfers: trip.transfers, hotels: trip.hotels, days: trip.days,
                custom_costs: trip.custom_costs, notes: trip.notes, cover_image_url: trip.cover_image_url,
                timeline_order: trip.timeline_order,
                client_phone: trip.client_phone, passengers: trip.passengers, ops_notes: trip.ops_notes,
                ops_client_label: trip.ops_client_label,
            });
            setTrip(data);
            setDirty(false);
        } catch (ex) {
            console.error(formatApiError(ex));
        } finally {
            setSaving(false);
        }
    };

    const exportPdf = () => {
        if (dirty) {
            // ensure save first
            save().then(() => {
                window.open(`${API}/trips/${id}/pdf`, "_blank");
            });
        } else {
            window.open(`${API}/trips/${id}/pdf`, "_blank");
        }
    };

    const createInvoice = async () => {
        try {
            if (dirty) await save();
            const { data } = await api.post(`/invoices/from-trip/${id}`);
            toast.success("Rechnung erstellt");
            navigate(`/invoices/${data.id}`);
        } catch (ex) {
            toast.error(formatApiError(ex));
        }
    };

    if (!trip) {
        return <AppLayout><div className="p-8 text-ink-secondary">Lade Reise…</div></AppLayout>;
    }

    return (
        <AppLayout>
            {/* Header */}
            <div className="flex items-start justify-between mb-8 gap-4">
                <div className="flex-1 min-w-0">
                    <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-ink-tertiary hover:text-navy mb-3 transition-colors" data-testid="back-to-dashboard">
                        <ChevronLeft className="w-3.5 h-3.5" /> Zurück zum Dashboard
                    </Link>
                    <input
                        value={trip.title || ""}
                        onChange={(e) => update({ title: e.target.value })}
                        placeholder="Reisetitel"
                        data-testid="trip-title-input"
                        className="text-3xl md:text-4xl font-bold tracking-tight bg-transparent border-0 border-b border-transparent hover:border-sand-300 focus:border-navy focus:outline-none transition-colors w-full pb-1"
                        style={{ fontFamily: "Space Grotesk" }}
                    />
                    <p className="text-sm text-ink-secondary mt-2">
                        <span className="font-medium text-ink">{trip.destination}</span> · {(trip.num_adults ?? trip.num_travelers ?? 0)} Erw{trip.children_ages?.length ? ` + ${trip.children_ages.length} Kind${trip.children_ages.length === 1 ? "" : "er"}` : ""} · {trip.currency}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-ink-tertiary mr-2 flex items-center gap-1.5" data-testid="save-status">
                        {saving ? (<><Loader2 className="w-3 h-3 animate-spin" /> speichert…</>) : dirty ? "Änderungen offen" : "Gespeichert"}
                    </span>
                    <button onClick={save} disabled={saving} data-testid="save-button" className="hidden sm:inline-flex px-4 py-2.5 rounded-md border border-sand-300 text-sm font-medium hover:bg-sand-deep items-center gap-2 transition-colors">
                        <Save className="w-4 h-4" /> Speichern
                    </button>
                    <button onClick={createInvoice} data-testid="create-invoice-button" className="bg-white border border-sand-300 text-ink hover:border-navy rounded-md px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors" title="Rechnung aus dieser Reise erstellen">
                        <FileText className="w-4 h-4" /> Rechnung
                    </button>
                    <button onClick={exportPdf} data-testid="export-pdf-button" className="bg-navy text-sand hover:bg-navy-hover rounded-md px-5 py-2.5 font-medium flex items-center gap-2 transition-colors shadow-card">
                        <FileDown className="w-4 h-4" /> PDF Export
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-1 border-b border-sand-300 mb-8" data-testid="trip-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                            className={`relative px-4 py-3 text-sm font-medium flex items-center gap-2 transition-colors ${
                                tab === t.id ? "text-navy" : "text-ink-secondary hover:text-ink"
                            }`}>
                            <Icon className="w-4 h-4" />
                            {t.label}
                            {tab === t.id && <span className="absolute -bottom-px left-0 right-0 h-[2px] bg-terracotta" />}
                        </button>
                    );
                })}
            </div>

            {/* Tab content */}
            <div className="animate-fade-up">
                {tab === "overview" && <OverviewTab trip={trip} update={update} />}
                {tab === "flight" && <FlightTab trip={trip} update={update} />}
                {tab === "transfer" && <TransferTab trip={trip} update={update} drivers={driverTemplates} />}
                {tab === "hotel" && <HotelTab trip={trip} update={update} hotelTemplates={hotelTemplates} />}
                {tab === "itinerary" && <ItineraryTab trip={trip} update={update} excursions={excursions} />}
                {tab === "preview" && <TimelinePreview trip={trip} update={update} />}
                {tab === "costs" && <CostsTab trip={trip} update={update} />}
            </div>
        </AppLayout>
    );
}

// ============ TABS ============

function OverviewTab({ trip, update }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="pt-card p-7 lg:col-span-2">
                <p className="pt-eyebrow mb-3">Stammdaten</p>
                <h2 className="pt-h3 mb-5">Kunde & Reise</h2>
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Kunde" value={trip.client_name || ""} onChange={(v) => update({ client_name: v })} testid="overview-client-name" />
                    <Field label="Kunden-E-Mail" type="email" value={trip.client_email || ""} onChange={(v) => update({ client_email: v })} testid="overview-client-email" />
                    <Field label="Kunden-Telefon" value={trip.client_phone || ""} onChange={(v) => update({ client_phone: v })} placeholder="+41 79 ..." testid="overview-client-phone" />
                    <Select label="Zielort" value={trip.destination} onChange={(v) => update({ destination: v })} options={["Hurghada", "Marsa Alam", "Sharm el-Sheikh", "Kairo", "Luxor"]} testid="overview-destination" />
                    <Select label="Status" value={trip.status} onChange={(v) => update({ status: v })} options={["draft", "sent", "confirmed"]} testid="overview-status" />
                    <Field type="date" label="Anreise" value={trip.start_date || ""} onChange={(v) => update({ start_date: v })} testid="overview-start" />
                    <Field type="date" label="Abreise" value={trip.end_date || ""} onChange={(v) => update({ end_date: v })} testid="overview-end" />
                    <Field type="number" label="Erwachsene" value={trip.num_adults ?? trip.num_travelers ?? 2} onChange={(v) => {
                        const n = parseInt(v);
                        update({ num_adults: Number.isFinite(n) ? Math.max(1, Math.min(20, n)) : 1 });
                    }} testid="overview-adults" />
                    <Select label="Währung" value={trip.currency} onChange={(v) => update({ currency: v })} options={["CHF", "EUR", "USD"]} testid="overview-currency" />
                </div>
                <div className="mt-5">
                    <ChildrenAgesPicker
                        ages={trip.children_ages || []}
                        onChange={(ages) => update({ children_ages: ages })}
                        idPrefix="overview"
                    />
                </div>
                <div className="mt-5">
                    <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Notizen</label>
                    <textarea rows={4} value={trip.notes || ""} onChange={(e) => update({ notes: e.target.value })} data-testid="overview-notes"
                        className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy" />
                </div>
                <div className="mt-6 pt-5 border-t border-sand-300">
                    <PassengersBlock trip={trip} update={update} />
                </div>
            </div>

            <div className="pt-card p-7">
                <p className="pt-eyebrow mb-3">Schnellübersicht</p>
                <h2 className="pt-h3 mb-5">Status</h2>
                <Stat label="Hotels" value={trip.hotels?.length || 0} />
                <Stat label="Transfers" value={trip.transfers?.length || 0} />
                <Stat label="Tage geplant" value={trip.days?.length || 0} />
                <Stat label="Aktivitäten" value={(trip.days || []).reduce((s, d) => s + (d.activities?.length || 0), 0)} />
                <div className="mt-6 pt-5 border-t border-sand-300">
                    <p className="pt-eyebrow mb-2">Gesamtpreis</p>
                    <p className="text-3xl font-bold text-terracotta" style={{ fontFamily: "Space Grotesk" }}>{formatMoney(trip.total_cost || 0, trip.currency)}</p>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="flex items-center justify-between py-2.5 border-b border-sand-300 last:border-0">
            <span className="text-sm text-ink-secondary">{label}</span>
            <span className="text-sm font-bold text-ink">{value}</span>
        </div>
    );
}

function FlightTab({ trip, update }) {
    const extras = trip.extra_flights || [];

    const addExtra = () => update({
        extra_flights: [...extras, { airline: "", flight_number: "", departure_airport: "", arrival_airport: "", departure_datetime: "", arrival_datetime: "", cabin_class: "Economy", price: 0, cost_price: 0, notes: "", image_url: "" }]
    });
    const setExtra = (i, data) => {
        const next = [...extras];
        next[i] = data;
        update({ extra_flights: next });
    };
    const removeExtra = (i) => update({ extra_flights: extras.filter((_, x) => x !== i) });

    return (
        <div className="space-y-6">
            <FlightCard label="Hinflug · CH → Ägypten" data={trip.outbound_flight || {}} onChange={(v) => update({ outbound_flight: v })} prefix="outbound" />
            {extras.map((ef, i) => (
                <div key={i} className="relative">
                    <FlightCard label={`Inland-Flug ${i + 1}`} data={ef} onChange={(v) => setExtra(i, v)} prefix={`extra-${i}`} />
                    <button
                        type="button"
                        onClick={() => removeExtra(i)}
                        data-testid={`extra-flight-${i}-delete`}
                        className="absolute top-7 right-16 text-sm text-ink-secondary hover:text-terracotta flex items-center gap-1.5 transition-colors"
                    >
                        <Trash2 className="w-4 h-4" /> Entfernen
                    </button>
                </div>
            ))}
            <FlightCard label="Rückflug · Ägypten → CH" data={trip.return_flight || {}} onChange={(v) => update({ return_flight: v })} prefix="return" />
            <div className="flex justify-center">
                <button
                    type="button"
                    onClick={addExtra}
                    data-testid="add-extra-flight"
                    className="bg-terracotta text-white hover:bg-terracotta-hover rounded-md px-5 py-2.5 font-medium flex items-center gap-2 shadow-card"
                >
                    <Plus className="w-4 h-4" /> Inland-Flug hinzufügen
                </button>
            </div>
        </div>
    );
}

function FlightCard({ label, data, onChange, prefix }) {
    const set = (k, v) => onChange({ ...data, [k]: v });
    return (
        <div className="pt-card p-7" data-testid={`flight-${prefix}`}>
            <div className="flex items-center justify-between mb-5">
                <div>
                    <p className="pt-eyebrow mb-2">Flug</p>
                    <h2 className="pt-h3">{label}</h2>
                </div>
                <Plane className="w-5 h-5 text-navy" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <Field label="Airline" value={data.airline || ""} onChange={(v) => set("airline", v)} testid={`${prefix}-airline`} />
                <Field label="Flugnummer" value={data.flight_number || ""} onChange={(v) => set("flight_number", v)} testid={`${prefix}-flightnum`} />
                <Select label="Klasse" value={data.cabin_class || "Economy"} onChange={(v) => set("cabin_class", v)} options={["Economy", "Premium Economy", "Business", "First"]} testid={`${prefix}-cabin`} />
                <Field label="Abflughafen" value={data.departure_airport || ""} onChange={(v) => set("departure_airport", v)} placeholder="ZRH" testid={`${prefix}-from`} />
                <Field label="Zielflughafen" value={data.arrival_airport || ""} onChange={(v) => set("arrival_airport", v)} placeholder="HRG" testid={`${prefix}-to`} />
                <Field type="number" label="Preis pro Person" value={data.price || 0} onChange={(v) => set("price", parseFloat(v || "0"))} testid={`${prefix}-price`} />
                <Field type="datetime-local" label="Abflug" value={data.departure_datetime || ""} onChange={(v) => set("departure_datetime", v)} testid={`${prefix}-depart-dt`} />
                <Field type="datetime-local" label="Ankunft" value={data.arrival_datetime || ""} onChange={(v) => set("arrival_datetime", v)} testid={`${prefix}-arrive-dt`} />
            </div>
            <InternalCostField
                label="Einkaufspreis pro Person"
                value={data.cost_price || 0}
                onChange={(v) => set("cost_price", v)}
                testid={`${prefix}-cost-price`}
            />
            <ChildPricingPair
                adultSale={data.price || 0}
                adultCost={data.cost_price || 0}
                childSale={data.child_price || 0}
                childCost={data.child_cost_price || 0}
                onChangeChildSale={(v) => set("child_price", v)}
                onChangeChildCost={(v) => set("child_cost_price", v)}
                saleLabel="Verkauf Kind"
                costLabel="Einkauf Kind"
                hint="Pro Person · Kinder <12"
                testidPrefix={prefix}
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                <ImageUploader
                    label="Flugzeug-Foto"
                    value={data.image_url || ""}
                    onChange={(v) => set("image_url", v)}
                    testid={`${prefix}-image`}
                />
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Notizen</label>
                    <textarea rows={5} value={data.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid={`${prefix}-notes`}
                        className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy resize-none" />
                </div>
            </div>
        </div>
    );
}

function TransferTab({ trip, update, drivers }) {
    const transfers = trip.transfers || [];
    const [pickerForIdx, setPickerForIdx] = useState(null);

    const add = () => update({ transfers: [...transfers, { id: crypto.randomUUID(), type: "Flughafen-Hotel", from_location: "", to_location: "", date: "", time: "", vehicle: "Privat-Van", price: 0, driver_name: "", company_name: "", mobile_no: "", notes: "", image_url: "" }] });
    const set = (i, patch) => {
        const next = [...transfers]; next[i] = { ...next[i], ...patch }; update({ transfers: next });
    };
    const remove = (i) => update({ transfers: transfers.filter((_, x) => x !== i) });

    const applyDriver = (i, driver) => {
        set(i, {
            driver_name: driver.name,
            company_name: driver.company_name || "",
            mobile_no: driver.mobile_no || "",
            vehicle: driver.vehicle || "",
            price: transfers[i].price || driver.price || 0,
            image_url: transfers[i].image_url || driver.image_url || "",
        });
        setPickerForIdx(null);
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <p className="pt-eyebrow mb-2">Transfer</p>
                    <h2 className="pt-h3">Transferleistungen</h2>
                </div>
                <button onClick={add} data-testid="add-transfer-button" className="bg-terracotta text-white hover:bg-terracotta-hover rounded-md px-4 py-2.5 text-sm font-medium flex items-center gap-2 shadow-card">
                    <Plus className="w-4 h-4" /> Transfer hinzufügen
                </button>
            </div>
            {transfers.length === 0 && <Empty msg="Noch keine Transfers angelegt." />}
            {transfers.map((tr, i) => (
                <div key={tr.id || i} className="pt-card p-6" data-testid={`transfer-row-${i}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-6 gap-3 mb-4">
                        <Select label="Typ" value={tr.type} onChange={(v) => set(i, { type: v })} options={["Flughafen-Hotel", "Hotel-Flughafen", "Hotel-Hotel", "Sonstiges"]} testid={`transfer-${i}-type`} />
                        <Field label="Von" value={tr.from_location} onChange={(v) => set(i, { from_location: v })} testid={`transfer-${i}-from`} />
                        <Field label="Nach" value={tr.to_location} onChange={(v) => set(i, { to_location: v })} testid={`transfer-${i}-to`} />
                        <Field type="date" label="Datum" value={tr.date} onChange={(v) => set(i, { date: v })} testid={`transfer-${i}-date`} />
                        <Field type="time" label="Zeit" value={tr.time} onChange={(v) => set(i, { time: v })} testid={`transfer-${i}-time`} />
                        <Field type="number" label="Preis" value={tr.price} onChange={(v) => set(i, { price: parseFloat(v || "0") })} testid={`transfer-${i}-price`} />
                    </div>
                    <InternalCostField
                        label="Einkaufspreis (gesamt)"
                        value={tr.cost_price || 0}
                        onChange={(v) => set(i, { cost_price: v })}
                        testid={`transfer-${i}-cost-price`}
                    />
                    <div className="pt-4 border-t border-sand-300">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <UserCircle2 className="w-4 h-4 text-navy" />
                                <p className="pt-eyebrow">Fahrer-Kontakt</p>
                            </div>
                            <button type="button" onClick={() => setPickerForIdx(i)} data-testid={`transfer-${i}-pick-driver`} className="text-xs bg-navy text-sand hover:bg-navy-hover rounded-md px-3 py-1.5 flex items-center gap-1.5 transition-colors">
                                <Library className="w-3 h-3" /> Aus Content-DB
                            </button>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                            <Field label="Fahrzeug" value={tr.vehicle} onChange={(v) => set(i, { vehicle: v })} placeholder="Privat-Van, Bus…" testid={`transfer-${i}-vehicle`} />
                            <Field label="Fahrer Name" value={tr.driver_name || ""} onChange={(v) => set(i, { driver_name: v })} placeholder="Mahmoud Ahmed" testid={`transfer-${i}-driver`} />
                            <Field label="Firma" value={tr.company_name || ""} onChange={(v) => set(i, { company_name: v })} placeholder="Egypt Travel Services" testid={`transfer-${i}-company`} />
                            <Field type="tel" label="Mobile Nr." value={tr.mobile_no || ""} onChange={(v) => set(i, { mobile_no: v })} placeholder="+20 100 123 4567" testid={`transfer-${i}-mobile`} />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4 pt-4 border-t border-sand-300">
                        <ImageUploader
                            label="Fahrzeug-Foto"
                            value={tr.image_url || ""}
                            onChange={(v) => set(i, { image_url: v })}
                            testid={`transfer-${i}-image`}
                        />
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Notizen</label>
                            <textarea rows={5} value={tr.notes || ""} onChange={(e) => set(i, { notes: e.target.value })} data-testid={`transfer-${i}-notes`}
                                className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy resize-none" />
                        </div>
                    </div>
                    <div className="flex justify-end pt-4 mt-4 border-t border-sand-300">
                        <button type="button" onClick={() => remove(i)} className="text-sm text-ink-secondary hover:text-terracotta flex items-center gap-1.5 transition-colors" data-testid={`transfer-${i}-delete`}>
                            <Trash2 className="w-4 h-4" /> Entfernen
                        </button>
                    </div>
                </div>
            ))}

            {pickerForIdx !== null && <DriverPicker drivers={drivers} onPick={(d) => applyDriver(pickerForIdx, d)} onClose={() => setPickerForIdx(null)} />}
        </div>
    );
}

function HotelTab({ trip, update, hotelTemplates }) {
    const hotels = trip.hotels || [];
    const [showPicker, setShowPicker] = useState(false);

    const add = () => update({
        hotels: [...hotels, { id: crypto.randomUUID(), name: "", location: trip.destination, room_type: "Doppelzimmer", board_type: "All Inclusive", check_in_date: trip.start_date || "", check_in_time: "15:00", check_out_date: trip.end_date || "", check_out_time: "11:00", nights: 0, price_per_night: 0, image_url: "", images: [], notes: "" }]
    });
    const addFromTemplate = (tpl) => {
        const newHotel = {
            id: crypto.randomUUID(),
            name: tpl.name,
            location: tpl.location,
            room_type: tpl.room_type,
            board_type: tpl.board_type,
            check_in_date: trip.start_date || "",
            check_in_time: "15:00",
            check_out_date: trip.end_date || "",
            check_out_time: "11:00",
            nights: 0,
            pricing_mode: tpl.pricing_mode || "flat",
            price_per_night: tpl.price_per_night,
            cost_per_night: tpl.cost_per_night || 0,
            child_price_per_night: tpl.child_price_per_night || 0,
            child_cost_per_night: tpl.child_cost_per_night || 0,
            image_url: tpl.image_url,
            images: tpl.images || [],
            notes: tpl.notes || tpl.description || "",
        };
        if (newHotel.check_in_date && newHotel.check_out_date) {
            const d = (new Date(newHotel.check_out_date) - new Date(newHotel.check_in_date)) / 86400000;
            if (!Number.isNaN(d) && d >= 0) newHotel.nights = Math.round(d);
        }
        update({ hotels: [...hotels, newHotel] });
        setShowPicker(false);
    };
    const set = (i, patch) => {
        const next = [...hotels];
        next[i] = { ...next[i], ...patch };
        if (patch.check_in_date || patch.check_out_date) {
            const ci = next[i].check_in_date, co = next[i].check_out_date;
            if (ci && co) {
                const d = (new Date(co) - new Date(ci)) / (1000 * 60 * 60 * 24);
                if (!Number.isNaN(d) && d >= 0) next[i].nights = Math.round(d);
            }
        }
        update({ hotels: next });
    };
    const remove = (i) => update({ hotels: hotels.filter((_, x) => x !== i) });

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <p className="pt-eyebrow mb-2">Unterkunft</p>
                    <h2 className="pt-h3">Hotels</h2>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setShowPicker(true)} data-testid="hotel-add-from-db" className="bg-navy text-sand hover:bg-navy-hover rounded-md px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors">
                        <Library className="w-4 h-4" /> Aus Content-DB
                    </button>
                    <button onClick={add} data-testid="add-hotel-button" className="bg-terracotta text-white hover:bg-terracotta-hover rounded-md px-4 py-2.5 text-sm font-medium flex items-center gap-2 shadow-card">
                        <Plus className="w-4 h-4" /> Neues Hotel
                    </button>
                </div>
            </div>
            {hotels.length === 0 && <Empty msg="Noch keine Hotels angelegt." />}
            {hotels.map((h, i) => (
                <div key={h.id || i} className="pt-card p-7" data-testid={`hotel-row-${i}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                        <div className="lg:col-span-2">
                            <Field label="Hotelname" value={h.name} onChange={(v) => set(i, { name: v })} testid={`hotel-${i}-name`} />
                        </div>
                        <Field label="Ort" value={h.location} onChange={(v) => set(i, { location: v })} testid={`hotel-${i}-location`} />
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Select label="Zimmer" value={h.room_type} onChange={(v) => set(i, { room_type: v })} options={["Doppelzimmer", "Doppelzimmer Meerblick", "Einzelzimmer", "Suite", "Junior Suite", "Familienzimmer", "Familien-Suite", "Bungalow", "Bungalow Beachfront", "Bungalow Gartenblick", "Villa", "Villa mit Pool", "Deluxe Suite Panoramic Pyramids & Sphinx View", "Executive Suite Panoramic Front Pyramids & Sphinx View"]} testid={`hotel-${i}-room`} />
                        <Select label="Verpflegung" value={h.board_type} onChange={(v) => set(i, { board_type: v })} options={["All Inclusive", "Halbpension", "Vollpension", "Frühstück", "Ohne"]} testid={`hotel-${i}-board`} />
                        <Field type="date" label="Check-in" value={h.check_in_date} onChange={(v) => set(i, { check_in_date: v })} testid={`hotel-${i}-checkin`} />
                        <Field type="time" label="Check-in Zeit" value={h.check_in_time} onChange={(v) => set(i, { check_in_time: v })} testid={`hotel-${i}-checkin-time`} />
                        <Field type="date" label="Check-out" value={h.check_out_date} onChange={(v) => set(i, { check_out_date: v })} testid={`hotel-${i}-checkout`} />
                        <Field type="time" label="Check-out Zeit" value={h.check_out_time} onChange={(v) => set(i, { check_out_time: v })} testid={`hotel-${i}-checkout-time`} />
                        <Field type="number" label="Nächte" value={h.nights} onChange={(v) => set(i, { nights: parseInt(v || "0") })} testid={`hotel-${i}-nights`} />
                        <PricingModeSelect
                            value={h.pricing_mode || "flat"}
                            onChange={(v) => set(i, { pricing_mode: v })}
                            testid={`hotel-${i}-pricing-mode`}
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <Field
                            type="number"
                            label={(h.pricing_mode || "flat") === "per_pax" ? "Verkauf pro Erwachsenem / Nacht" : "Preis / Nacht (Pauschal)"}
                            value={h.price_per_night}
                            onChange={(v) => set(i, { price_per_night: parseFloat(v || "0") })}
                            testid={`hotel-${i}-price`}
                        />
                    </div>
                    <InternalCostField
                        label={(h.pricing_mode || "flat") === "per_pax" ? "Einkauf pro Erwachsenem / Nacht" : "Einkaufspreis pro Nacht (Pauschal)"}
                        value={h.cost_per_night || 0}
                        onChange={(v) => set(i, { cost_per_night: v })}
                        testid={`hotel-${i}-cost-per-night`}
                    />
                    <ChildPricingPair
                        adultSale={h.price_per_night || 0}
                        adultCost={h.cost_per_night || 0}
                        childSale={h.child_price_per_night || 0}
                        childCost={h.child_cost_per_night || 0}
                        onChangeChildSale={(v) => set(i, { child_price_per_night: v })}
                        onChangeChildCost={(v) => set(i, { child_cost_per_night: v })}
                        saleLabel={(h.pricing_mode || "flat") === "per_pax" ? "Verkauf pro Kind / Nacht" : "Verkauf Aufschlag Kind / Nacht"}
                        costLabel={(h.pricing_mode || "flat") === "per_pax" ? "Einkauf pro Kind / Nacht" : "Einkauf Aufschlag Kind / Nacht"}
                        hint={(h.pricing_mode || "flat") === "per_pax"
                            ? "Pro Kind <12 / Nacht (leer = Erwachsenen-Preis)"
                            : "Aufschlag pro Kind <12 / Nacht (z.B. extra Bett)"}
                        testidPrefix={`hotel-${i}`}
                    />
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                        <div className="lg:col-span-2">
                            <ActivityGallery
                                main={h.image_url || ""}
                                extras={h.images || []}
                                onChangeMain={(v) => set(i, { image_url: v })}
                                onChangeExtras={(arr) => set(i, { images: arr })}
                                testidPrefix={`hotel-${i}`}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Notizen</label>
                            <textarea rows={5} value={h.notes || ""} onChange={(e) => set(i, { notes: e.target.value })} data-testid={`hotel-${i}-notes`}
                                className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy resize-none" />
                        </div>
                    </div>
                    <div className="flex justify-between items-center pt-4 mt-5 border-t border-sand-300">
                        <p className="text-sm text-ink-secondary">
                            Subtotal: <span className="font-semibold text-ink">{formatMoney(
                                hotelSubtotal(h, trip),
                                trip.currency
                            )}</span>
                        </p>
                        <button type="button" onClick={() => remove(i)} className="text-sm text-ink-secondary hover:text-terracotta flex items-center gap-1.5 transition-colors" data-testid={`hotel-${i}-delete`}>
                            <Trash2 className="w-4 h-4" /> Entfernen
                        </button>
                    </div>
                </div>
            ))}

            {showPicker && <HotelPicker hotels={hotelTemplates} onPick={addFromTemplate} onClose={() => setShowPicker(false)} />}
        </div>
    );
}

function ItineraryTab({ trip, update, excursions }) {
    const days = trip.days || [];
    const [pickerForDay, setPickerForDay] = useState(null);

    const addDay = () => {
        const lastDate = days.length > 0 ? days[days.length - 1].date : trip.start_date;
        let nextDate = "";
        if (lastDate) {
            const d = new Date(lastDate);
            d.setDate(d.getDate() + (days.length === 0 ? 0 : 1));
            nextDate = d.toISOString().slice(0, 10);
        }
        update({ days: [...days, { id: crypto.randomUUID(), day_number: days.length + 1, date: nextDate, title: "", activities: [], notes: "" }] });
    };

    const setDay = (i, patch) => {
        const next = [...days]; next[i] = { ...next[i], ...patch }; update({ days: next });
    };
    const removeDay = (i) => {
        update({ days: days.filter((_, x) => x !== i).map((d, idx) => ({ ...d, day_number: idx + 1 })) });
    };

    const addActivity = (di, exc = null) => {
        const next = [...days];
        next[di].activities = [...(next[di].activities || []), exc ? {
            id: crypto.randomUUID(), time: "", title: exc.title, description: exc.description,
            location: exc.location, duration: exc.duration, price: exc.price,
            child_price: exc.child_price || 0, cost_price: 0, child_cost_price: 0,
            excursion_id: exc.id, image_url: exc.image_url, images: exc.images || [],
        } : {
            id: crypto.randomUUID(), time: "", title: "", description: "", location: "", duration: "", price: 0,
            child_price: 0, cost_price: 0, child_cost_price: 0,
            excursion_id: null, image_url: "", images: [],
        }];
        update({ days: next });
    };
    const setActivity = (di, ai, patch) => {
        const next = [...days];
        next[di].activities[ai] = { ...next[di].activities[ai], ...patch };
        update({ days: next });
    };
    const removeActivity = (di, ai) => {
        const next = [...days];
        next[di].activities = next[di].activities.filter((_, x) => x !== ai);
        update({ days: next });
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <p className="pt-eyebrow mb-2">Programm</p>
                    <h2 className="pt-h3">Tag-für-Tag Plan</h2>
                </div>
                <button onClick={addDay} data-testid="add-day-button" className="bg-terracotta text-white hover:bg-terracotta-hover rounded-md px-4 py-2.5 text-sm font-medium flex items-center gap-2 shadow-card">
                    <Plus className="w-4 h-4" /> Tag hinzufügen
                </button>
            </div>
            {days.length === 0 && <Empty msg="Noch keine Tage angelegt. Beginne mit Tag 1." />}

            {days.map((d, di) => (
                <div key={d.id || di} className="pt-card overflow-visible" data-testid={`day-block-${di}`}>
                    <div className="bg-sand-deep px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <span className="w-10 h-10 rounded-full bg-navy text-sand flex items-center justify-center font-bold text-sm" style={{ fontFamily: "Space Grotesk" }}>{d.day_number}</span>
                            <div className="flex items-center gap-3 flex-wrap">
                                <input type="date" value={d.date} onChange={(e) => setDay(di, { date: e.target.value })} data-testid={`day-${di}-date`}
                                    className="bg-white border border-sand-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                                <input type="text" value={d.title} onChange={(e) => setDay(di, { title: e.target.value })} placeholder="Tagestitel (z.B. Anreise)" data-testid={`day-${di}-title`}
                                    className="bg-white border border-sand-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy w-72" />
                            </div>
                        </div>
                        <button type="button" onClick={() => removeDay(di)} className="text-ink-secondary hover:text-terracotta p-1.5 rounded transition-colors" data-testid={`day-${di}-delete`}>
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="px-6 py-5 space-y-3">
                        {(d.activities || []).map((a, ai) => (
                            <div key={a.id || ai} className="p-4 rounded-md border border-sand-300 bg-sand-100 space-y-3" data-testid={`activity-${di}-${ai}`}>
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
                                    <input type="date" value={a.date || ""} onChange={(e) => setActivity(di, ai, { date: e.target.value })} data-testid={`activity-${di}-${ai}-date`}
                                        title="Datum (optional, sonst Tag des Plans)"
                                        className="lg:col-span-2 bg-white border border-sand-300 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                                    <input type="time" value={a.time} onChange={(e) => setActivity(di, ai, { time: e.target.value })} data-testid={`activity-${di}-${ai}-time`}
                                        className="lg:col-span-1 bg-white border border-sand-300 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                                    <input type="text" value={a.title} onChange={(e) => setActivity(di, ai, { title: e.target.value })} placeholder="Aktivität" data-testid={`activity-${di}-${ai}-title`}
                                        className="lg:col-span-3 bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                                    <input type="text" value={a.description} onChange={(e) => setActivity(di, ai, { description: e.target.value })} placeholder="Kurzbeschreibung" data-testid={`activity-${di}-${ai}-desc`}
                                        className="lg:col-span-3 bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                                    <input type="number" step="0.01" value={a.price} onChange={(e) => setActivity(di, ai, { price: parseFloat(e.target.value || "0") })} placeholder="Preis p.P." data-testid={`activity-${di}-${ai}-price`}
                                        className="lg:col-span-2 bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                                    <button type="button" onClick={() => removeActivity(di, ai)} className="lg:col-span-1 p-2 rounded hover:bg-terracotta/10 text-ink-secondary hover:text-terracotta transition-colors" data-testid={`activity-${di}-${ai}-delete`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <ActivityGallery
                                    main={a.image_url || ""}
                                    extras={a.images || []}
                                    onChangeMain={(v) => setActivity(di, ai, { image_url: v })}
                                    onChangeExtras={(arr) => setActivity(di, ai, { images: arr })}
                                    testidPrefix={`activity-${di}-${ai}`}
                                />
                                <ActivityPricing
                                    a={a}
                                    onPatch={(patch) => setActivity(di, ai, patch)}
                                    testidPrefix={`activity-${di}-${ai}`}
                                />
                            </div>
                        ))}

                        <div className="flex flex-wrap gap-2 pt-2">
                            <button type="button" onClick={() => addActivity(di)} data-testid={`day-${di}-add-activity`} className="text-sm border border-sand-300 hover:border-navy text-ink-secondary hover:text-navy rounded-md px-3 py-1.5 flex items-center gap-1.5 transition-colors">
                                <Plus className="w-3.5 h-3.5" /> Eigene Aktivität
                            </button>
                            <button type="button" onClick={() => setPickerForDay(di)} data-testid={`day-${di}-add-from-db`} className="text-sm bg-navy text-sand hover:bg-navy-hover rounded-md px-3 py-1.5 flex items-center gap-1.5 transition-colors">
                                <Library className="w-3.5 h-3.5" /> Aus Content-DB
                            </button>
                        </div>
                    </div>
                </div>
            ))}

            {pickerForDay !== null && (
                <ExcursionPicker excursions={excursions} onPick={(exc) => { addActivity(pickerForDay, exc); setPickerForDay(null); }} onClose={() => setPickerForDay(null)} />
            )}
        </div>
    );
}

function ExcursionPicker({ excursions, onPick, onClose }) {
    const [q, setQ] = useState("");
    const filtered = excursions.filter((e) => e.title.toLowerCase().includes(q.toLowerCase()) || (e.location || "").toLowerCase().includes(q.toLowerCase()));
    return (
        <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-hover w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="excursion-picker">
                <div className="p-6 border-b border-sand-300">
                    <p className="pt-eyebrow mb-2">Aus Content-DB einfügen</p>
                    <h2 className="pt-h3 mb-4">Ausflug wählen</h2>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche nach Titel oder Ort…" data-testid="picker-search"
                        className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy" />
                </div>
                <div className="flex-1 overflow-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filtered.map((e) => (
                        <button key={e.id} onClick={() => onPick(e)} data-testid={`picker-pick-${e.id}`} className="text-left pt-card-interactive">
                            <div className="aspect-[4/3] bg-sand-deep relative overflow-hidden">
                                {e.image_url && <img src={resolveImageUrl(e.image_url)} alt={e.title} className="w-full h-full object-cover" loading="lazy" />}
                                <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded bg-white/95 text-navy">{e.category}</span>
                            </div>
                            <div className="p-4">
                                <p className="text-xs text-ink-tertiary">{e.location} · {e.duration}</p>
                                <p className="font-semibold text-ink text-sm mt-1 line-clamp-2">{e.title}</p>
                                <p className="text-terracotta font-bold mt-2 text-sm">{e.currency === "EUR" ? "€" : e.currency} {e.price}</p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function HotelPicker({ hotels, onPick, onClose }) {
    const [q, setQ] = useState("");
    const filtered = hotels.filter((h) => h.name.toLowerCase().includes(q.toLowerCase()) || (h.location || "").toLowerCase().includes(q.toLowerCase()));
    return (
        <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-hover w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="hotel-picker">
                <div className="p-6 border-b border-sand-300">
                    <p className="pt-eyebrow mb-2">Hotel aus Content-DB</p>
                    <h2 className="pt-h3 mb-4">Hotel wählen</h2>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche nach Name oder Ort…" data-testid="hotel-picker-search"
                        className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy" />
                </div>
                <div className="flex-1 overflow-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filtered.length === 0 && <p className="col-span-2 text-center text-ink-secondary py-12">Keine Hotels in der Content-DB. Lege welche unter "Content-DB → Hotels" an.</p>}
                    {filtered.map((h) => (
                        <button key={h.id} onClick={() => onPick(h)} data-testid={`hotel-picker-pick-${h.id}`} className="text-left pt-card-interactive">
                            <div className="aspect-[4/3] bg-sand-deep relative overflow-hidden">
                                {h.image_url && <img src={resolveImageUrl(h.image_url)} alt={h.name} className="w-full h-full object-cover" loading="lazy" />}
                                <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded bg-white/95 text-navy">{h.board_type}</span>
                            </div>
                            <div className="p-4">
                                <p className="text-xs text-ink-tertiary">{h.location} · {h.room_type}</p>
                                <p className="font-semibold text-ink text-sm mt-1 line-clamp-2">{h.name}</p>
                                <p className="text-terracotta font-bold mt-2 text-sm">{h.currency === "EUR" ? "€" : h.currency} {h.price_per_night} <span className="text-ink-tertiary text-[10px] font-normal">/ Nacht</span></p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function DriverPicker({ drivers, onPick, onClose }) {
    const [q, setQ] = useState("");
    const filtered = drivers.filter((d) => d.name.toLowerCase().includes(q.toLowerCase()) || (d.company_name || "").toLowerCase().includes(q.toLowerCase()) || (d.location || "").toLowerCase().includes(q.toLowerCase()));
    return (
        <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-hover w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="driver-picker">
                <div className="p-6 border-b border-sand-300">
                    <p className="pt-eyebrow mb-2">Fahrer aus Content-DB</p>
                    <h2 className="pt-h3 mb-4">Fahrer wählen</h2>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche nach Name, Firma oder Ort…" data-testid="driver-picker-search"
                        className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy" />
                </div>
                <div className="flex-1 overflow-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {filtered.length === 0 && <p className="col-span-2 text-center text-ink-secondary py-12">Keine Fahrer in der Content-DB. Lege welche unter "Content-DB → Fahrer" an.</p>}
                    {filtered.map((d) => (
                        <button key={d.id} onClick={() => onPick(d)} data-testid={`driver-picker-pick-${d.id}`} className="text-left pt-card-interactive p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-navy text-sand flex items-center justify-center flex-shrink-0">
                                    <UserCircle2 className="w-5 h-5" strokeWidth={1.5} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold text-ink text-sm truncate" style={{ fontFamily: "Space Grotesk" }}>{d.name}</p>
                                    <p className="text-[11px] text-ink-secondary truncate flex items-center gap-1"><Building2 className="w-3 h-3" />{d.company_name || "—"}</p>
                                </div>
                            </div>
                            <p className="text-[11px] text-ink-secondary flex items-center gap-1"><Phone className="w-3 h-3 text-navy" /> <span className="font-mono">{d.mobile_no || "—"}</span></p>
                            <p className="text-[11px] text-ink-secondary mt-0.5">{d.vehicle} · {d.location}</p>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function TimelinePreview({ trip, update }) {
    const [sortMode, setSortMode] = useState("flow"); // "flow" | "date" | "manual"
    const events = useMemo(() => {
        const out = [];
        const of = trip.outbound_flight || {};
        const rf = trip.return_flight || {};

        if (of.departure_datetime || of.airline) {
            const dt = of.departure_datetime || "";
            out.push({
                key: "flight-out",
                type: "flight-out",
                flowOrder: 1,
                date: dt ? dt.slice(0, 10) : trip.start_date,
                time: dt ? dt.slice(11, 16) : "",
                title: `Hinflug · ${of.airline || ""} ${of.flight_number || ""}`.trim(),
                description: `${of.departure_airport || "—"} → ${of.arrival_airport || "—"}${of.cabin_class ? ` · ${of.cabin_class}` : ""}`,
                price: of.price ? of.price * Math.max(1, trip.num_travelers || 1) : 0,
                image: of.image_url,
                icon: "plane-out",
                accent: "navy",
            });
        }

        const looksLikeAirport = (s) => /flugh|airport/i.test(s || "");
        for (const t of trip.transfers || []) {
            if (t.from_location || t.to_location || t.date) {
                const isReturn = looksLikeAirport(t.to_location) && !looksLikeAirport(t.from_location);
                out.push({
                    key: `transfer:${t.id}`,
                    type: "transfer",
                    flowOrder: isReturn ? 5 : 2,
                    date: t.date || "",
                    time: t.time || "",
                    title: `Transfer · ${t.type || ""}`,
                    description: `${t.from_location || "—"} → ${t.to_location || "—"}${t.driver_name ? ` · Fahrer: ${t.driver_name}` : ""}${t.mobile_no ? ` · ${t.mobile_no}` : ""}`,
                    sub: t.vehicle || "",
                    price: t.price || 0,
                    image: t.image_url,
                    icon: "bus",
                    accent: "navy",
                });
            }
        }

        for (const h of trip.hotels || []) {
            if (h.check_in_date) {
                out.push({
                    key: `hotel-in:${h.id}`,
                    type: "hotel-checkin",
                    flowOrder: 3,
                    date: h.check_in_date,
                    time: h.check_in_time || "15:00",
                    title: `Check-in · ${h.name || "Hotel"}`,
                    description: `${h.location || ""} · ${h.room_type || ""} · ${h.board_type || ""}`,
                    sub: h.notes || "",
                    image: h.image_url,
                    price: 0,
                    icon: "hotel-in",
                    accent: "terracotta",
                });
            }
            if (h.check_out_date) {
                out.push({
                    key: `hotel-out:${h.id}`,
                    type: "hotel-checkout",
                    flowOrder: 3,
                    date: h.check_out_date,
                    time: h.check_out_time || "11:00",
                    title: `Check-out · ${h.name || "Hotel"}`,
                    description: `${h.nights || 0} Nächte · Subtotal ${formatMoney((h.nights || 0) * (h.price_per_night || 0), trip.currency)}`,
                    icon: "hotel-out",
                    accent: "ink",
                });
            }
        }

        for (const d of trip.days || []) {
            for (const a of (d.activities || [])) {
                out.push({
                    key: `activity:${a.id}`,
                    type: "activity",
                    flowOrder: 4,
                    date: a.date || d.date || "",
                    time: a.time || "",
                    dayNumber: d.day_number,
                    title: a.title || "Aktivität",
                    description: a.description || "",
                    price: a.price ? a.price * Math.max(1, trip.num_travelers || 1) : 0,
                    image: a.image_url,
                    icon: "compass",
                    accent: "terracotta",
                });
            }
        }

        // Inland-Flüge — flowOrder 4 (mid-trip), sortiert nach Datum
        (trip.extra_flights || []).forEach((ef, idx) => {
            if (ef.airline || ef.departure_datetime) {
                const dt = ef.departure_datetime || "";
                out.push({
                    key: `extra-flight:${idx}`,
                    type: "flight-extra",
                    flowOrder: 4,
                    date: dt ? dt.slice(0, 10) : "",
                    time: dt ? dt.slice(11, 16) : "",
                    title: `Inland-Flug · ${ef.airline || ""} ${ef.flight_number || ""}`.trim(),
                    description: `${ef.departure_airport || "—"} → ${ef.arrival_airport || "—"}${ef.cabin_class ? ` · ${ef.cabin_class}` : ""}`,
                    price: ef.price ? ef.price * Math.max(1, trip.num_travelers || 1) : 0,
                    image: ef.image_url,
                    icon: "plane-out",
                    accent: "navy",
                });
            }
        });

        if (rf.departure_datetime || rf.airline) {
            const dt = rf.departure_datetime || "";
            out.push({
                key: "flight-return",
                type: "flight-return",
                flowOrder: 6,
                date: dt ? dt.slice(0, 10) : trip.end_date,
                time: dt ? dt.slice(11, 16) : "",
                title: `Rückflug · ${rf.airline || ""} ${rf.flight_number || ""}`.trim(),
                description: `${rf.departure_airport || "—"} → ${rf.arrival_airport || "—"}${rf.cabin_class ? ` · ${rf.cabin_class}` : ""}`,
                price: rf.price ? rf.price * Math.max(1, trip.num_travelers || 1) : 0,
                image: rf.image_url,
                icon: "plane-in",
                accent: "navy",
            });
        }

        const byDateTime = (a, b) => {
            const da = a.date || "9999-12-31";
            const db = b.date || "9999-12-31";
            if (da !== db) return da.localeCompare(db);
            const ta = a.time || "99:99";
            const tb = b.time || "99:99";
            return ta.localeCompare(tb);
        };

        if (sortMode === "flow") {
            return out.sort((a, b) => {
                if (a.flowOrder !== b.flowOrder) return a.flowOrder - b.flowOrder;
                return byDateTime(a, b);
            });
        }
        if (sortMode === "manual") {
            const order = trip.timeline_order || [];
            const idx = (k) => {
                const i = order.indexOf(k);
                return i === -1 ? Number.MAX_SAFE_INTEGER : i;
            };
            return out.sort((a, b) => {
                const ia = idx(a.key);
                const ib = idx(b.key);
                if (ia !== ib) return ia - ib;
                return byDateTime(a, b);
            });
        }
        return out.sort(byDateTime);
    }, [trip, sortMode]);

    const moveEvent = (fromIdx, toIdx) => {
        if (toIdx < 0 || toIdx >= events.length) return;
        const newOrderKeys = events.map((e) => e.key);
        const [k] = newOrderKeys.splice(fromIdx, 1);
        newOrderKeys.splice(toIdx, 0, k);
        if (update) update({ timeline_order: newOrderKeys });
    };

    const grouped = useMemo(() => {
        const map = new Map();
        if (sortMode === "flow") {
            const labels = {
                1: "1 · Hinflug",
                2: "2 · Transfer Flughafen → Hotel",
                3: "3 · Hotel",
                4: "4 · Ausflüge & Aktivitäten",
                5: "5 · Transfer Hotel → Flughafen",
                6: "6 · Rückflug",
            };
            for (const ev of events) {
                const key = `${ev.flowOrder}::${labels[ev.flowOrder] || "Sonstiges"}`;
                if (!map.has(key)) map.set(key, []);
                map.get(key).push(ev);
            }
        } else if (sortMode === "manual") {
            // alle Events in einer flachen Liste, ohne Gruppierung
            map.set("manual::Eigene Reihenfolge", events);
        } else {
            for (const ev of events) {
                const key = ev.date || "ohne-datum";
                if (!map.has(key)) map.set(key, []);
                map.get(key).push(ev);
            }
        }
        return Array.from(map.entries());
    }, [events, sortMode]);

    if (events.length === 0) {
        return <Empty msg="Füge Flug, Hotel, Transfer oder Aktivitäten hinzu, um die Timeline zu sehen." />;
    }

    const startDate = trip.start_date || (events[0] && events[0].date);

    const dayNumberFor = (dateStr) => {
        if (!startDate || !dateStr || dateStr === "ohne-datum") return null;
        const diff = Math.round((new Date(dateStr) - new Date(startDate)) / 86400000) + 1;
        return diff > 0 ? diff : null;
    };

    return (
        <div className="pt-card p-8 lg:p-12">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                    <p className="pt-eyebrow mb-3">Visuelle Vorschau</p>
                    <h2 className="pt-h2 mb-2">Timeline</h2>
                    <p className="pt-body">
                        {sortMode === "manual"
                            ? "Reihenfolge frei festlegen — mit ↑↓ verschieben."
                            : "Alle Reisebausteine — Flug, Transfer, Hotel und Aktivitäten — chronologisch im Reisefluss."}
                    </p>
                </div>
                <div className="flex bg-sand-deep rounded-lg p-1" data-testid="timeline-sort-toggle">
                    <button
                        type="button"
                        onClick={() => setSortMode("flow")}
                        data-testid="timeline-sort-flow"
                        className={`px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-[0.12em] transition-all ${sortMode === "flow" ? "bg-white text-navy shadow-sm" : "text-ink-secondary hover:text-ink"}`}
                    >
                        Reisefluss
                    </button>
                    <button
                        type="button"
                        onClick={() => setSortMode("date")}
                        data-testid="timeline-sort-date"
                        className={`px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-[0.12em] transition-all ${sortMode === "date" ? "bg-white text-navy shadow-sm" : "text-ink-secondary hover:text-ink"}`}
                    >
                        Nach Datum
                    </button>
                    <button
                        type="button"
                        onClick={() => setSortMode("manual")}
                        data-testid="timeline-sort-manual"
                        className={`px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-[0.12em] transition-all ${sortMode === "manual" ? "bg-white text-navy shadow-sm" : "text-ink-secondary hover:text-ink"}`}
                    >
                        Manuell
                    </button>
                </div>
            </div>
            <div className="relative pl-10 border-l-2 border-sand-300 ml-3 space-y-12 mt-10">
                {grouped.map(([groupKey, dayEvents], i) => {
                    if (sortMode === "flow") {
                        const stage = groupKey.split("::")[0];
                        const label = groupKey.split("::")[1] || "—";
                        return (
                            <div key={groupKey + i} className="relative" data-testid={`timeline-stage-${stage}`}>
                                <span className="absolute -left-[46px] top-0 w-8 h-8 rounded-full bg-navy text-sand flex items-center justify-center text-xs font-bold" style={{ fontFamily: "Space Grotesk" }}>{stage}</span>
                                <h3 className="pt-h3 mb-3">{label.replace(/^\d+\s·\s/, "")}</h3>
                                <div className="space-y-3">
                                    {dayEvents.map((ev, ei) => <TimelineEvent key={ev.key + ei} ev={ev} currency={trip.currency} />)}
                                </div>
                            </div>
                        );
                    }
                    if (sortMode === "manual") {
                        return (
                            <div key={groupKey + i} className="relative">
                                <span className="absolute -left-[46px] top-0 w-8 h-8 rounded-full bg-terracotta text-white flex items-center justify-center text-xs font-bold" style={{ fontFamily: "Space Grotesk" }}>↕</span>
                                <h3 className="pt-h3 mb-3">Eigene Reihenfolge</h3>
                                <div className="space-y-3">
                                    {dayEvents.map((ev, ei) => (
                                        <div key={ev.key} className="flex items-start gap-2">
                                            <div className="flex flex-col gap-1 pt-2">
                                                <button
                                                    type="button"
                                                    onClick={() => moveEvent(ei, ei - 1)}
                                                    disabled={ei === 0}
                                                    data-testid={`timeline-move-up-${ei}`}
                                                    className="w-8 h-8 rounded-md border border-sand-300 bg-white hover:bg-sand-deep disabled:opacity-30 disabled:cursor-not-allowed text-ink transition-colors flex items-center justify-center text-sm"
                                                    aria-label="Nach oben"
                                                >▲</button>
                                                <span className="text-[10px] text-center text-ink-tertiary font-semibold">{ei + 1}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => moveEvent(ei, ei + 1)}
                                                    disabled={ei === dayEvents.length - 1}
                                                    data-testid={`timeline-move-down-${ei}`}
                                                    className="w-8 h-8 rounded-md border border-sand-300 bg-white hover:bg-sand-deep disabled:opacity-30 disabled:cursor-not-allowed text-ink transition-colors flex items-center justify-center text-sm"
                                                    aria-label="Nach unten"
                                                >▼</button>
                                            </div>
                                            <div className="flex-1">
                                                <TimelineEvent ev={ev} currency={trip.currency} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }
                    const dateKey = groupKey;
                    const dayNum = dayNumberFor(dateKey);
                    return (
                        <div key={dateKey + i} className="relative" data-testid={`timeline-day-${i}`}>
                            <span className="absolute -left-[46px] top-0 w-8 h-8 rounded-full bg-white border-2 border-navy flex items-center justify-center text-xs font-bold" style={{ fontFamily: "Space Grotesk" }}>{dayNum || "?"}</span>
                            <div className="flex items-baseline gap-3 mb-3 flex-wrap">
                                <h3 className="pt-h3">
                                    {dateKey === "ohne-datum" ? "Ohne Datum" : new Date(dateKey).toLocaleDateString("de-CH", { weekday: "long", day: "2-digit", month: "long" })}
                                </h3>
                                {dayNum && <span className="text-xs text-ink-tertiary">Tag {dayNum}</span>}
                            </div>
                            <div className="space-y-3">
                                {dayEvents.map((ev, ei) => <TimelineEvent key={ev.key + ei} ev={ev} currency={trip.currency} />)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function TimelineEvent({ ev, currency }) {
    const accentClass = {
        terracotta: "border-l-terracotta",
        navy: "border-l-navy",
        ink: "border-l-ink",
    }[ev.accent || "navy"];

    const Icon = ({ type }) => {
        const cls = "w-5 h-5";
        switch (type) {
            case "plane-out": return <Plane className={cls + " -rotate-45"} strokeWidth={1.75} />;
            case "plane-in": return <Plane className={cls + " rotate-[135deg]"} strokeWidth={1.75} />;
            case "bus": return <Bus className={cls} strokeWidth={1.75} />;
            case "hotel-in": return <Hotel className={cls} strokeWidth={1.75} />;
            case "hotel-out": return <Hotel className={cls + " opacity-60"} strokeWidth={1.75} />;
            case "compass":
            default: return <CalendarDays className={cls} strokeWidth={1.75} />;
        }
    };

    const showImage = !!ev.image;

    return (
        <div className={`flex gap-4 p-4 rounded-md border border-sand-300 border-l-4 ${accentClass} bg-sand-100`} data-testid={`timeline-event-${ev.type}`}>
            {showImage ? (
                <img src={resolveImageUrl(ev.image)} alt="" className="w-24 h-24 rounded-md object-cover flex-shrink-0" loading="lazy" />
            ) : (
                <div className={`w-24 h-24 rounded-md flex items-center justify-center flex-shrink-0 ${ev.accent === "terracotta" ? "bg-terracotta/10 text-terracotta" : ev.accent === "ink" ? "bg-sand-deep text-ink-secondary" : "bg-navy/10 text-navy"}`}>
                    <Icon type={ev.icon} />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                    {ev.time && <span className="text-xs font-mono font-semibold text-terracotta">{ev.time}</span>}
                    <span className="text-base font-semibold text-ink">{ev.title}</span>
                </div>
                <p className="text-sm text-ink-secondary line-clamp-2">{ev.description}</p>
                {ev.sub && <p className="text-xs text-ink-tertiary mt-1 italic">{ev.sub}</p>}
                {ev.price > 0 && <p className="text-xs font-semibold text-navy mt-2">{formatMoney(ev.price, currency)}</p>}
            </div>
        </div>
    );
}

function CostsTab({ trip, update }) {
    const customCosts = trip.custom_costs || [];
    const travelers = Math.max(1, trip.num_travelers || 1);
    const adults = Math.max(0, Number(trip.num_adults ?? trip.num_travelers ?? 0));
    const ages = trip.children_ages || [];
    const kidsUnder12 = ages.filter((a) => a != null && Number(a) < 12).length;
    const kidsOver12 = ages.length - kidsUnder12;
    const fullCount = adults + kidsOver12;

    const lines = useMemo(() => {
        const out = [];
        const of = trip.outbound_flight || {};
        const rf = trip.return_flight || {};

        const pushFlight = (label, f, details) => {
            if (!(f.price > 0)) return;
            const childPrice = Number(f.child_price || 0);
            if (childPrice > 0 && kidsUnder12 > 0) {
                if (fullCount > 0) {
                    out.push({ label, details: `${details} · Erwachsene`, qty: fullCount, unit: f.price, total: f.price * fullCount });
                }
                out.push({ label, details: `${details} · Kinder <12`, qty: kidsUnder12, unit: childPrice, total: childPrice * kidsUnder12 });
            } else {
                out.push({ label, details, qty: travelers, unit: f.price, total: f.price * travelers });
            }
        };

        pushFlight("Hinflug", of, `${of.airline || ""} ${of.flight_number || ""}`.trim() || "—");
        pushFlight("Rückflug", rf, `${rf.airline || ""} ${rf.flight_number || ""}`.trim() || "—");
        for (const ef of (trip.extra_flights || [])) {
            pushFlight("Inland-Flug", ef, `${ef.airline || ""} ${ef.departure_airport || ""}→${ef.arrival_airport || ""}`.trim());
        }
        for (const h of trip.hotels || []) {
            const n = h.nights || 0;
            if (n <= 0) continue;
            const adult = h.price_per_night || 0;
            const child = h.child_price_per_night || 0;
            const mode = (h.pricing_mode || "flat");
            if (mode === "per_pax") {
                if (child > 0 && kidsUnder12 > 0) {
                    // Separate Zeilen — unterschiedliche Preise
                    if (adult > 0 && fullCount > 0) {
                        out.push({
                            label: `Hotel: ${h.name || "—"}`,
                            details: `${h.board_type || "—"} · Erwachsene`,
                            qty: `${fullCount}× × ${n}`,
                            unit: adult,
                            total: adult * fullCount * n,
                        });
                    }
                    out.push({
                        label: `Hotel: ${h.name || "—"}`,
                        details: `${h.board_type || "—"} · Kinder <12`,
                        qty: `${kidsUnder12}× × ${n}`,
                        unit: child,
                        total: child * kidsUnder12 * n,
                    });
                } else if (adult > 0) {
                    // Kein separater Kinderpreis → eine Zeile für alle Personen
                    const totalPax = fullCount + kidsUnder12;
                    out.push({
                        label: `Hotel: ${h.name || "—"}`,
                        details: h.board_type,
                        qty: `${totalPax}× × ${n}`,
                        unit: adult,
                        total: adult * totalPax * n,
                    });
                }
            } else {
                if (adult > 0) {
                    out.push({ label: `Hotel: ${h.name || "—"}`, details: h.board_type, qty: `${n} Nächte`, unit: adult, total: n * adult });
                }
                if (child > 0 && kidsUnder12 > 0) {
                    out.push({
                        label: `Hotel: ${h.name || "—"}`,
                        details: `Kinder-Aufschlag <12 · ${n} Nächte`,
                        qty: `${kidsUnder12}× × ${n}`,
                        unit: child,
                        total: child * kidsUnder12 * n,
                    });
                }
            }
        }
        for (const t of trip.transfers || []) {
            if (t.price > 0) out.push({ label: "Transfer", details: `${t.from_location} → ${t.to_location}`, qty: 1, unit: t.price, total: t.price });
        }
        for (const d of trip.days || []) {
            for (const a of (d.activities || [])) {
                if (!(a.price > 0)) continue;
                const childPrice = Number(a.child_price || 0);
                if (childPrice > 0 && kidsUnder12 > 0) {
                    if (fullCount > 0) {
                        out.push({
                            label: a.title || "—",
                            details: `Tag ${d.day_number} · Erwachsene`,
                            qty: fullCount,
                            unit: a.price,
                            total: a.price * fullCount,
                        });
                    }
                    out.push({
                        label: a.title || "—",
                        details: `Tag ${d.day_number} · Kinder <12`,
                        qty: kidsUnder12,
                        unit: childPrice,
                        total: childPrice * kidsUnder12,
                    });
                } else {
                    out.push({ label: a.title || "—", details: `Tag ${d.day_number}`, qty: travelers, unit: a.price, total: a.price * travelers });
                }
            }
        }
        for (const c of customCosts) {
            const q = c.quantity || 1;
            out.push({ label: c.label || "Sonstige Position", details: "", qty: q, unit: c.amount, total: (c.amount || 0) * q });
        }
        return out;
    }, [trip, travelers, customCosts, fullCount, kidsUnder12]);

    const total = lines.reduce((s, l) => s + l.total, 0);

    const addCustom = () => update({ custom_costs: [...customCosts, { id: crypto.randomUUID(), label: "", amount: 0, quantity: 1 }] });
    const setCustom = (i, patch) => {
        const next = [...customCosts]; next[i] = { ...next[i], ...patch }; update({ custom_costs: next });
    };
    const removeCustom = (i) => update({ custom_costs: customCosts.filter((_, x) => x !== i) });

    return (
        <div className="space-y-6">
            <div className="pt-card overflow-hidden" data-testid="cost-table">
                <div className="px-6 py-4 bg-navy text-sand grid grid-cols-12 text-xs font-bold uppercase tracking-[0.14em]">
                    <div className="col-span-4">Position</div>
                    <div className="col-span-3">Details</div>
                    <div className="col-span-2 text-right">Anzahl</div>
                    <div className="col-span-1 text-right">Einzel</div>
                    <div className="col-span-2 text-right">Total</div>
                </div>
                {lines.length === 0 && <div className="p-8 text-center text-ink-secondary">Noch keine Kostenpositionen.</div>}
                {lines.map((l, i) => (
                    <div key={i} className="px-6 py-3.5 grid grid-cols-12 text-sm border-b border-sand-300 last:border-0 items-center hover:bg-sand-100">
                        <div className="col-span-4 font-medium text-ink truncate">{l.label}</div>
                        <div className="col-span-3 text-ink-secondary truncate">{l.details}</div>
                        <div className="col-span-2 text-right text-ink-secondary">{l.qty}</div>
                        <div className="col-span-1 text-right text-ink-secondary">{formatMoney(l.unit, trip.currency)}</div>
                        <div className="col-span-2 text-right font-semibold text-ink">{formatMoney(l.total, trip.currency)}</div>
                    </div>
                ))}
                <div className="px-6 py-5 bg-terracotta text-white grid grid-cols-12 items-center">
                    <div className="col-span-10 font-bold uppercase tracking-[0.14em] text-sm">Gesamtpreis</div>
                    <div className="col-span-2 text-right text-2xl font-bold" style={{ fontFamily: "Space Grotesk" }} data-testid="cost-total">{formatMoney(total, trip.currency)}</div>
                </div>
                <div className="px-6 py-3 bg-sand-deep grid grid-cols-12 text-sm">
                    <div className="col-span-10 text-ink-secondary">Pro Person ({travelers}×)</div>
                    <div className="col-span-2 text-right font-semibold text-navy" data-testid="cost-per-person">{formatMoney(total / travelers, trip.currency)}</div>
                </div>
            </div>

            <div className="pt-card p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="pt-h3">Zusatzpositionen</h3>
                    <button onClick={addCustom} data-testid="add-custom-cost" className="text-sm border border-sand-300 hover:border-navy text-ink-secondary hover:text-navy rounded-md px-3 py-1.5 flex items-center gap-1.5 transition-colors">
                        <Plus className="w-3.5 h-3.5" /> Position
                    </button>
                </div>
                {customCosts.length === 0 && <p className="text-sm text-ink-tertiary">Reiseversicherung, Visa, Trinkgeld etc. hier hinzufügen.</p>}
                {customCosts.map((c, i) => (
                    <div key={c.id || i} className="grid grid-cols-12 gap-3 mb-3 items-end">
                        <input type="text" value={c.label} onChange={(e) => setCustom(i, { label: e.target.value })} placeholder="Bezeichnung" data-testid={`custom-${i}-label`}
                            className="col-span-6 bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                        <input type="number" value={c.quantity} onChange={(e) => setCustom(i, { quantity: parseInt(e.target.value || "1") })} placeholder="Anzahl" data-testid={`custom-${i}-qty`}
                            className="col-span-2 bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                        <input type="number" step="0.01" value={c.amount} onChange={(e) => setCustom(i, { amount: parseFloat(e.target.value || "0") })} placeholder="Einzelpreis" data-testid={`custom-${i}-amount`}
                            className="col-span-3 bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                        <button onClick={() => removeCustom(i)} data-testid={`custom-${i}-delete`} className="col-span-1 p-2 rounded hover:bg-terracotta/10 text-ink-secondary hover:text-terracotta transition-colors">
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>

            <ProfitSummary trip={trip} saleTotal={total} />
        </div>
    );
}

function ProfitSummary({ trip, saleTotal }) {
    const internalCost = trip.internal_cost ?? 0;
    const profit = saleTotal - internalCost;
    const margin = saleTotal > 0 ? (profit / saleTotal) * 100 : 0;
    const isPositive = profit >= 0;
    return (
        <div className="pt-card p-7 border-l-4 border-navy" data-testid="profit-summary">
            <div className="flex items-start gap-3 mb-4">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] bg-navy text-sand px-2 py-1 rounded-sm">Intern</span>
                <div>
                    <h3 className="pt-h3">Marge & Gewinn</h3>
                    <p className="text-xs text-ink-tertiary">Diese Werte sind nur für dich sichtbar — sie erscheinen nicht im Kunden-PDF.</p>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
                <div className="bg-sand-100 border border-sand-300 rounded-md px-4 py-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary mb-1">Verkauf (Kunde)</p>
                    <p className="text-2xl font-bold text-ink" style={{ fontFamily: "Space Grotesk" }}>{formatMoney(saleTotal, trip.currency)}</p>
                </div>
                <div className="bg-sand-100 border border-sand-300 rounded-md px-4 py-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary mb-1">Einkauf (intern)</p>
                    <p className="text-2xl font-bold text-ink-secondary" style={{ fontFamily: "Space Grotesk" }} data-testid="profit-cost">{formatMoney(internalCost, trip.currency)}</p>
                </div>
                <div className={`rounded-md px-4 py-3 border ${isPositive ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary mb-1">Gewinn ({margin.toFixed(1)}%)</p>
                    <p className={`text-2xl font-bold ${isPositive ? "text-emerald-700" : "text-red-700"}`} style={{ fontFamily: "Space Grotesk" }} data-testid="profit-amount">{formatMoney(profit, trip.currency)}</p>
                </div>
            </div>
        </div>
    );
}

function InternalCostField({ label, value, onChange, testid }) {
    return (
        <div className="mt-4 pt-4 border-t border-dashed border-sand-300 bg-sand-100/50 rounded-md px-4 py-3">
            <div className="flex items-start gap-3">
                <span className="text-[9px] font-bold uppercase tracking-[0.16em] bg-navy text-sand px-1.5 py-1 rounded-sm flex-shrink-0">Intern</span>
                <div className="flex-1">
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-1.5">{label}</label>
                    <input
                        type="number"
                        step="0.01"
                        value={value || 0}
                        onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
                        data-testid={testid}
                        className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-ink focus:outline-none focus:ring-2 focus:ring-navy"
                        placeholder="Einkaufspreis (nur intern, nicht im PDF)"
                    />
                    <p className="text-[10px] text-ink-tertiary mt-1.5">Erscheint nicht im Kunden-PDF — nur für deine Margenberechnung.</p>
                </div>
            </div>
        </div>
    );
}

function PricingModeSelect({ value, onChange, testid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Preis-Modus</label>
            <select
                value={value || "flat"}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full bg-white border border-sand-300 rounded-md px-3 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-navy"
            >
                <option value="flat">Pauschal / Nacht</option>
                <option value="per_pax">Pro Person / Nacht</option>
            </select>
        </div>
    );
}

function hotelSubtotal(h, trip) {
    const n = h.nights || 0;
    if (n <= 0) return 0;
    const ages = trip.children_ages || [];
    const adults = Number(trip.num_adults ?? trip.num_travelers ?? 0);
    const kidsUnder12 = ages.filter((a) => a != null && Number(a) < 12).length;
    const kidsOver12 = ages.length - kidsUnder12;
    const fullCount = adults + kidsOver12;
    const adult = h.price_per_night || 0;
    const child = h.child_price_per_night || 0;
    if ((h.pricing_mode || "flat") === "per_pax") {
        const eff = child > 0 ? child : adult;
        return (adult * fullCount + eff * kidsUnder12) * n;
    }
    return adult * n + child * kidsUnder12 * n;
}

function ChildPricingPair({
    adultSale, adultCost, childSale, childCost,
    onChangeChildSale, onChangeChildCost,
    saleLabel = "Verkauf Kind", costLabel = "Einkauf Kind",
    hint = "Pro Person · Kinder <12",
    testidPrefix = "child-pricing",
}) {
    const num = (e) => parseFloat(e.target.value || "0");
    const fill50 = () => {
        onChangeChildSale(Math.round((Number(adultSale || 0) * 0.5) * 100) / 100);
        onChangeChildCost(Math.round((Number(adultCost || 0) * 0.5) * 100) / 100);
    };
    return (
        <div className="mt-3 bg-sand-100/50 border border-dashed border-sand-300 rounded-md px-4 py-3">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-secondary">
                    Preise · <span className="normal-case font-normal text-ink-tertiary">{hint}</span>
                </p>
                <button
                    type="button"
                    onClick={fill50}
                    data-testid={`${testidPrefix}-child-fill-50`}
                    className="text-[10px] uppercase tracking-[0.14em] text-navy hover:text-navy-hover border border-navy/30 hover:border-navy rounded px-2 py-1 transition-colors"
                    title="Kinderpreise auf 50% setzen"
                >
                    50 % vorschlagen
                </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <PriceInput
                    label={saleLabel}
                    value={childSale || 0}
                    onChange={(e) => onChangeChildSale(num(e))}
                    testid={`${testidPrefix}-child-price`}
                />
                <PriceInput
                    label={costLabel}
                    intern
                    value={childCost || 0}
                    onChange={(e) => onChangeChildCost(num(e))}
                    testid={`${testidPrefix}-child-cost-price`}
                />
            </div>
            <p className="text-[10px] text-ink-tertiary mt-2">Leer (0) → keine Auswirkung, alle zahlen voll.</p>
        </div>
    );
}

function ActivityPricing({ a, onPatch, testidPrefix }) {
    const num = (e) => parseFloat(e.target.value || "0");
    const fillChild50 = () => onPatch({
        child_price: Math.round((Number(a.price || 0) * 0.5) * 100) / 100,
        child_cost_price: Math.round((Number(a.cost_price || 0) * 0.5) * 100) / 100,
    });
    return (
        <div className="mt-4 pt-4 border-t border-dashed border-sand-300 bg-sand-100/50 rounded-md px-4 py-3 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-secondary">
                    Preise · <span className="normal-case font-normal text-ink-tertiary">Kinder unter 12</span>
                </p>
                <button
                    type="button"
                    onClick={fillChild50}
                    data-testid={`${testidPrefix}-fill-50`}
                    className="text-[10px] uppercase tracking-[0.14em] text-navy hover:text-navy-hover border border-navy/30 hover:border-navy rounded px-2 py-1 transition-colors"
                    title="Kinderpreise auf 50% des Erwachsenenpreises setzen"
                >
                    50 % vorschlagen
                </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <PriceInput
                    label="Verkauf Kind"
                    value={a.child_price || 0}
                    onChange={(e) => onPatch({ child_price: num(e) })}
                    testid={`${testidPrefix}-child-price`}
                    hint="Sichtbar im PDF"
                />
                <PriceInput
                    label="Einkauf Kind"
                    intern
                    value={a.child_cost_price || 0}
                    onChange={(e) => onPatch({ child_cost_price: num(e) })}
                    testid={`${testidPrefix}-child-cost-price`}
                />
                <PriceInput
                    label="Einkauf Erwachsen"
                    intern
                    value={a.cost_price || 0}
                    onChange={(e) => onPatch({ cost_price: num(e) })}
                    testid={`${testidPrefix}-cost-price`}
                />
                <div className="text-[10px] text-ink-tertiary self-end pb-2">
                    Verkaufspreis Erwachsen oben in der Hauptzeile.<br />
                    Kinderpreise leer (0) → alle zahlen voll.
                </div>
            </div>
        </div>
    );
}

function PriceInput({ label, value, onChange, testid, intern = false, hint = "" }) {
    return (
        <div>
            <div className="flex items-center gap-1.5 mb-1.5">
                {intern && (
                    <span className="text-[8px] font-bold uppercase tracking-[0.16em] bg-navy text-sand px-1 py-0.5 rounded-sm">Intern</span>
                )}
                <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-secondary">{label}</label>
            </div>
            <input
                type="number"
                step="0.01"
                value={value || 0}
                onChange={onChange}
                data-testid={testid}
                className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-navy"
            />
            {hint && <p className="text-[9px] text-ink-tertiary mt-1">{hint}</p>}
        </div>
    );
}

// ============ helpers ============
function Field({ label, value, onChange, type = "text", required = false, placeholder = "", testid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">{label}</label>
            <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
                className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-navy" />
        </div>
    );
}
function Select({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">{label}</label>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 text-ink focus:outline-none focus:ring-2 focus:ring-navy">
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}
function Empty({ msg }) {
    return <div className="bg-white border border-dashed border-sand-300 rounded-lg p-10 text-center text-ink-secondary text-sm">{msg}</div>;
}

function ActivityGallery({ main, extras, onChangeMain, onChangeExtras, testidPrefix }) {
    const list = Array.isArray(extras) ? extras : [];
    const totalCount = (main ? 1 : 0) + list.length;
    const MAX = 5;
    const canAdd = totalCount < MAX;

    const addSlot = () => {
        if (!main) {
            // erstes Bild → main
            return;
        }
        if (list.length < MAX - 1) {
            onChangeExtras([...list, ""]);
        }
    };
    const setExtra = (i, v) => {
        const next = [...list];
        next[i] = v;
        onChangeExtras(next);
    };
    const removeExtra = (i) => {
        onChangeExtras(list.filter((_, x) => x !== i));
    };
    const removeMain = () => {
        // wenn extras vorhanden: erstes extra wird neues main
        if (list.length > 0) {
            const [first, ...rest] = list;
            onChangeMain(first);
            onChangeExtras(rest);
        } else {
            onChangeMain("");
        }
    };

    return (
        <div className="bg-white rounded-md border border-sand-300 p-3">
            <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-secondary">Bilder ({totalCount}/{MAX})</p>
                {canAdd && (main || list.length > 0) && (
                    <button
                        type="button"
                        onClick={addSlot}
                        data-testid={`${testidPrefix}-add-image`}
                        className="text-[11px] font-semibold uppercase tracking-[0.12em] text-navy hover:text-terracotta transition-colors flex items-center gap-1"
                    >
                        + Bild hinzufügen
                    </button>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-2">
                {/* Hauptbild-Slot (immer sichtbar) */}
                <div>
                    <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-ink-tertiary mb-1">Hauptbild</p>
                    <div className="relative">
                        <ImageUploader
                            label=""
                            value={main}
                            onChange={onChangeMain}
                            testid={`${testidPrefix}-image-0`}
                        />
                        {main && (
                            <button
                                type="button"
                                onClick={removeMain}
                                data-testid={`${testidPrefix}-image-0-remove`}
                                className="absolute top-1 right-1 bg-white/90 hover:bg-terracotta hover:text-white text-ink rounded-full w-6 h-6 flex items-center justify-center text-xs shadow-sm transition-colors"
                                aria-label="Bild entfernen"
                            >×</button>
                        )}
                    </div>
                </div>
                {/* Zusätzliche Bilder */}
                {list.map((url, i) => (
                    <div key={i}>
                        <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-ink-tertiary mb-1">Bild {i + 2}</p>
                        <div className="relative">
                            <ImageUploader
                                label=""
                                value={url}
                                onChange={(v) => setExtra(i, v)}
                                testid={`${testidPrefix}-image-${i + 1}`}
                            />
                            {url && (
                                <button
                                    type="button"
                                    onClick={() => removeExtra(i)}
                                    data-testid={`${testidPrefix}-image-${i + 1}-remove`}
                                    className="absolute top-1 right-1 bg-white/90 hover:bg-terracotta hover:text-white text-ink rounded-full w-6 h-6 flex items-center justify-center text-xs shadow-sm transition-colors"
                                    aria-label="Bild entfernen"
                                >×</button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PassengersBlock({ trip, update }) {
    const passengers = trip.passengers || [];
    const setPax = (arr) => update({ passengers: arr });
    const addPax = () => setPax([...passengers, { name: "", passport_image_url: "", notes: "" }]);
    const updatePax = (idx, patch) => setPax(passengers.map((p, i) => i === idx ? { ...p, ...patch } : p));
    const removePax = (idx) => setPax(passengers.filter((_, i) => i !== idx));
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <p className="pt-eyebrow">Passagiere & Passfotos</p>
                <button type="button" onClick={addPax} data-testid="add-passenger"
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-navy hover:text-navy-hover border border-navy/30 hover:border-navy rounded px-2 py-1 transition-colors">
                    <Plus className="w-3.5 h-3.5" /> Passagier
                </button>
            </div>
            {passengers.length === 0 && (
                <p className="text-xs text-ink-tertiary italic">Noch keine Passagiere hinzugefügt.</p>
            )}
            <div className="space-y-3">
                {passengers.map((p, idx) => (
                    <div key={idx} className="bg-sand-100/50 border border-sand-300 rounded-md p-3" data-testid={`passenger-${idx}`}>
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-24">
                                <ImageUploader
                                    label="Pass"
                                    value={p.passport_image_url || ""}
                                    onChange={(v) => updatePax(idx, { passport_image_url: v })}
                                    testid={`passenger-${idx}-passport`}
                                />
                            </div>
                            <div className="flex-1 space-y-2">
                                <input type="text" value={p.name || ""} onChange={(e) => updatePax(idx, { name: e.target.value })}
                                    placeholder="Name (wie im Pass)"
                                    data-testid={`passenger-${idx}-name`}
                                    className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                                <input type="text" value={p.notes || ""} onChange={(e) => updatePax(idx, { notes: e.target.value })}
                                    placeholder="Notizen (Allergie, Sprache, Reisepass-Nr …)"
                                    data-testid={`passenger-${idx}-notes`}
                                    className="w-full bg-white border border-sand-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy" />
                            </div>
                            <button type="button" onClick={() => removePax(idx)} data-testid={`passenger-${idx}-remove`}
                                className="p-1.5 rounded hover:bg-terracotta/10 text-ink-secondary hover:text-terracotta transition-colors">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


function ChildrenAgesPicker({ ages, onChange, idPrefix = "children" }) {
    const list = Array.isArray(ages) ? ages : [];
    const count = list.length;
    const setCount = (n) => {
        const next = Math.max(0, Math.min(5, n));
        if (next === count) return;
        if (next > count) {
            onChange([...list, ...Array(next - count).fill(0)]);
        } else {
            onChange(list.slice(0, next));
        }
    };
    const setAge = (idx, val) => {
        const n = parseInt(val);
        const safe = Number.isFinite(n) ? Math.max(0, Math.min(17, n)) : 0;
        const next = [...list];
        next[idx] = safe;
        onChange(next);
    };
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Kinder (max. 5)</label>
            <div className="flex items-center gap-2" data-testid={`${idPrefix}-children-count-row`}>
                <button
                    type="button"
                    onClick={() => setCount(count - 1)}
                    disabled={count === 0}
                    data-testid={`${idPrefix}-children-minus`}
                    className="w-9 h-9 rounded-md border border-sand-300 text-ink hover:bg-sand-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    aria-label="Weniger Kinder"
                >−</button>
                <div className="px-4 py-2 rounded-md border border-sand-300 bg-white min-w-[3rem] text-center font-semibold text-ink" data-testid={`${idPrefix}-children-count`}>
                    {count}
                </div>
                <button
                    type="button"
                    onClick={() => setCount(count + 1)}
                    disabled={count >= 5}
                    data-testid={`${idPrefix}-children-plus`}
                    className="w-9 h-9 rounded-md border border-sand-300 text-ink hover:bg-sand-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    aria-label="Mehr Kinder"
                >+</button>
                <span className="ml-2 text-xs text-ink-tertiary">Kind{count === 1 ? "" : "er"}</span>
            </div>
            {count > 0 && (
                <div className="mt-3 grid grid-cols-5 gap-2">
                    {list.map((age, i) => (
                        <div key={i}>
                            <label className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary mb-1">Kind {i + 1} (Alter)</label>
                            <input
                                type="number"
                                min="0"
                                max="17"
                                value={Number.isFinite(age) ? age : 0}
                                onChange={(e) => setAge(i, e.target.value)}
                                data-testid={`${idPrefix}-child-age-${i}`}
                                className="w-full bg-white border border-sand-300 rounded-md px-2.5 py-2 text-ink focus:outline-none focus:ring-2 focus:ring-navy text-sm"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
